import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { movies, moviesByCategory } from '@/mocks/movies';
import { categories, genres, years, regions, qualities } from '@/mocks/categories';
import type { Movie } from '@/mocks/movies';

type FilterKey = 'genre' | 'year' | 'region' | 'quality';

const filterConfigs: { key: FilterKey; label: string; options: string[] }[] = [
  { key: 'genre', label: '类型', options: genres },
  { key: 'year', label: '年份', options: years },
  { key: 'region', label: '地区', options: regions },
  { key: 'quality', label: '画质', options: qualities },
];

const categoryLabels: Record<string, string> = {
  all: '全部',
  movie: '电影',
  tv: '电视剧',
  anime: '动漫',
  variety: '综艺',
};

export default function CategoryPage() {
  const { categoryId = 'all' } = useParams<{ categoryId: string }>();
  const [activeFilters, setActiveFilters] = useState<Record<FilterKey, string>>({
    genre: '全部',
    year: '全部',
    region: '全部',
    quality: '全部',
  });
  const [sortBy, setSortBy] = useState<'rating' | 'year' | 'default'>('default');
  const [visibleCount, setVisibleCount] = useState(12);

  const categoryName = categoryLabels[categoryId] || '全部';

  useEffect(() => {
    document.title = `${categoryName} - 蘑菇影视高清在线观看 | 4K蓝光无广告`;
    window.scrollTo({ top: 0, behavior: 'instant' });
    setActiveFilters({ genre: '全部', year: '全部', region: '全部', quality: '全部' });
    setVisibleCount(12);
  }, [categoryId]);

  const filteredMovies = useMemo(() => {
    let result: Movie[] = categoryId === 'all' ? [...movies] : moviesByCategory(categoryId);

    if (activeFilters.genre !== '全部') {
      result = result.filter(m => m.genres.includes(activeFilters.genre));
    }
    if (activeFilters.year !== '全部') {
      result = result.filter(m => m.year === parseInt(activeFilters.year));
    }
    if (activeFilters.region !== '全部') {
      result = result.filter(m => m.region === activeFilters.region);
    }
    if (activeFilters.quality !== '全部') {
      result = result.filter(m => m.quality === activeFilters.quality);
    }

    if (sortBy === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'year') {
      result.sort((a, b) => b.year - a.year);
    }

    return result;
  }, [categoryId, activeFilters, sortBy]);

  const displayedMovies = filteredMovies.slice(0, visibleCount);
  const hasMore = visibleCount < filteredMovies.length;

  const toggleFilter = (key: FilterKey, value: string) => {
    setActiveFilters(prev => ({ ...prev, [key]: prev[key] === value ? '全部' : value }));
  };

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="px-4 md:px-6 lg:px-8 py-6">
            <div className="max-w-[1440px] mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: categoryName },
                ]}
              />

              <div className="mt-4">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50">
                  {categoryName}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  共找到 <strong className="text-primary-400">{filteredMovies.length}</strong> 部作品
                </p>
              </div>

              <div className="mt-6 flex items-center gap-1.5 flex-wrap">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={`/category/${cat.id}`}
                    className={`px-4 py-1.5 text-sm rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      categoryId === cat.id
                        ? 'bg-primary-500 text-foreground-50'
                        : 'bg-background-100 text-foreground-400 hover:text-foreground-200 hover:bg-background-200 border border-background-200/50'
                    }`}
                  >
                    {cat.name}
                  </a>
                ))}
              </div>

              <div className="mt-6 space-y-3">
                {filterConfigs.map((config) => (
                  <div key={config.key} className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-foreground-500 w-10 flex-shrink-0 whitespace-nowrap">
                      {config.label}
                    </span>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {config.options.map((option) => (
                        <button
                          key={option}
                          onClick={() => toggleFilter(config.key, option)}
                          className={`px-3 py-1 text-xs rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                            activeFilters[config.key] === option
                              ? 'bg-accent-500/20 text-accent-400 border border-accent-500/30'
                              : 'bg-background-100 text-foreground-400 hover:text-foreground-200 border border-background-200/30 hover:border-background-300/50'
                          }`}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-500">排序：</span>
                  {[
                    { value: 'default', label: '默认' },
                    { value: 'rating', label: '评分最高' },
                    { value: 'year', label: '最新发布' },
                  ].map((s) => (
                    <button
                      key={s.value}
                      onClick={() => setSortBy(s.value as typeof sortBy)}
                      className={`px-3 py-1 text-xs rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        sortBy === s.value
                          ? 'bg-secondary-500/20 text-secondary-400'
                          : 'text-foreground-400 hover:text-foreground-200'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
                {displayedMovies.map((movie) => (
                  <MovieCard
                    key={movie.id}
                    id={movie.id}
                    title={movie.title}
                    poster={movie.poster}
                    year={movie.year}
                    rating={movie.rating}
                    quality={movie.quality}
                    category={movie.category}
                    genres={movie.genres}
                  />
                ))}
              </div>

              {filteredMovies.length === 0 && (
                <div className="py-20 text-center">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                    <i className="ri-search-line text-2xl text-foreground-500"></i>
                  </div>
                  <p className="text-foreground-400 text-sm">没有找到符合条件的影视作品</p>
                  <p className="text-foreground-500 text-xs mt-1">请尝试调整筛选条件</p>
                </div>
              )}

              {hasMore && (
                <div className="mt-8 text-center">
                  <button
                    onClick={() => setVisibleCount(prev => prev + 12)}
                    className="px-8 py-2.5 bg-background-100 hover:bg-background-200 border border-background-200/50 hover:border-primary-500/30 text-foreground-300 hover:text-foreground-100 text-sm rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    加载更多
                  </button>
                </div>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}